import axios from 'axios';

import {
  addProducts,
  applyCoupon,
  BasketExceptionResponse,
  BasketExceptionType,
  BasketItem,
  createBasket,
  createBasketFromReference,
  deleteBasket,
  getBasket,
  getCurrentCustomerBaskets,
  normalizeSFCCLocale,
  patchProduct,
  ProductService,
  recalculateBasket,
  removeCoupon,
  removeProduct,
  setEngravingMessage,
  setQuantity,
  SFCCBasket,
  SiteSettings,
} from '@gn-audio/jss-core';

const ENSURE_BASKET_MAX_TRY = 6;

export class SFCCBasketService {
  public storage: Storage;
  private isLoggedIn: () => Promise<boolean>;

  constructor(
    private readonly settings: SiteSettings,
    isLoggedIn: () => Promise<boolean>
  ) {
    this.storage = localStorage;
    this.isLoggedIn = isLoggedIn;
  }

  public async patchProduct(
    itemId: string,
    quantity: number,
    services?: ProductService[],
    engravingMessage?: string
  ): Promise<SFCCBasket> {
    return await patchProduct(itemId, quantity, this.ensureBasket, engravingMessage, services);
  }

  public async addProducts(items: BasketItem[]): Promise<SFCCBasket> {
    return await addProducts(items, this.ensureBasket);
  }

  public async applyCoupon(couponCode: string): Promise<SFCCBasket> {
    return await applyCoupon(couponCode, this.ensureBasket);
  }

  public async removeProduct(itemId: string): Promise<SFCCBasket> {
    return await removeProduct(itemId, this.ensureBasket);
  }

  public async removeCoupon(couponItemId: string): Promise<SFCCBasket> {
    return await removeCoupon(couponItemId, this.ensureBasket);
  }

  public async setQuantity(itemId: string, sku: string, quantity: number): Promise<SFCCBasket> {
    return await setQuantity(itemId, sku, quantity, this.ensureBasket);
  }

  public async setEngravingMessage(
    itemId: string,
    quantity: number,
    engravingMessage: string
  ): Promise<SFCCBasket> {
    return await setEngravingMessage(itemId, quantity, engravingMessage, this.ensureBasket);
  }

  public async transferBasketToLoggedUser(
    guestCustomerId: string | undefined,
    basketId: string | undefined
  ): Promise<SFCCBasket> {
    if (!this.isLoggedIn()) {
      return this.ensureCurrentUserBasket();
    }
    if (!guestCustomerId || !basketId) {
      return this.ensureCurrentUserBasket();
    }

    await this.removeCurrentCustomerBaskets();
    return await createBasketFromReference(basketId, guestCustomerId);
  }

  public async ensureCurrentUserBasket(): Promise<SFCCBasket> {
    const basket = await this.ensureBasket();

    if (basket.c_geCCHash) {
      if (basket.c_geCCHash.includes(basket.currency)) {
        return basket;
      }
      // hack this needs to be done to hardly say sfcc to recalculate currency when there is transfer of basket from different currency to another one..
      // otherwise number will remain the same without applying currency courses
      return await recalculateBasket(basket.basket_id);
    }
    return basket;
  }

  async removeCurrentCustomerBaskets() {
    const baskets = await getCurrentCustomerBaskets();
    const removalActions = baskets.map((basket) => deleteBasket(basket.basket_id));
    return Promise.all(removalActions);
  }

  private async ensureBasket(tryNumber = 0): Promise<SFCCBasket> {
    if (tryNumber > ENSURE_BASKET_MAX_TRY) {
      throw 'Max number of getting basket exceeded';
    }

    const baskets = await getCurrentCustomerBaskets();
    try {
      if (baskets.length) {
        return await getBasket(baskets[0].basket_id);
      } else {
        return await createBasket();
      }
    } catch (error: unknown) {
      if (!axios.isAxiosError<BasketExceptionResponse>(error)) {
        throw error;
      }
      const basketException = error.response?.data;
      if (!basketException) throw error;

      const { type } = basketException.fault;

      // If basket stored in storage is invalid and/or user has another one.
      if (type === BasketExceptionType.CustomerBasketsQuotaExceededException) {
        const [customerBasketId] = basketException.fault.arguments['basketIds'].split(',');

        const customerBasket: SFCCBasket = await getBasket(customerBasketId);
        if (compareLocales(this.settings.sfcc?.shopApiLocale, customerBasket.c_localeID)) {
          return customerBasket;
        } else {
          await deleteBasket(customerBasketId);
        }
        return await this.ensureBasket(++tryNumber);
      }

      // Any other exceptions should be thrown
      throw basketException;
    }
  }
}

export const compareLocales = (locale1 = '', locale2 = ''): boolean => {
  const normalizedLocale1 = normalizeSFCCLocale(locale1).toLowerCase();
  const normalizedLocale2 = normalizeSFCCLocale(locale2).toLowerCase();

  return normalizedLocale1 === normalizedLocale2;
};
